/**
 * Utility functions for distance calculations
 * Centralizes all geographic distance calculations using the Haversine formula
 */

/**
 * Earth's radius in meters
 */
export const EARTH_RADIUS_METERS = 6371e3

/**
 * Calculate distance between two points using the Haversine formula
 * @param lat1 - Latitude of first point
 * @param lon1 - Longitude of first point  
 * @param lat2 - Latitude of second point
 * @param lon2 - Longitude of second point
 * @returns Distance in meters
 */
export function getDistanceBetweenPoints(
  lat1: number, 
  lon1: number, 
  lat2: number, 
  lon2: number
): number {
  const φ1 = lat1 * Math.PI / 180
  const φ2 = lat2 * Math.PI / 180
  const Δφ = (lat2 - lat1) * Math.PI / 180
  const Δλ = (lon2 - lon1) * Math.PI / 180

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return EARTH_RADIUS_METERS * c
}

/**
 * Calculate total distance for a route with multiple coordinates
 * @param coordinates - Array of [longitude, latitude] pairs
 * @returns Total distance in kilometers
 */
export function calculateRouteDistance(coordinates: [number, number][]): number {
  if (!coordinates || coordinates.length < 2) {
    return 0
  }
  
  let distance = 0
  for (let i = 1; i < coordinates.length; i++) {
    const [lon1, lat1] = coordinates[i - 1]
    const [lon2, lat2] = coordinates[i]
    distance += getDistanceBetweenPoints(lat1, lon1, lat2, lon2)
  }
  
  return distance / 1000 // Convert to km
}

/**
 * Calculate cumulative distances along a route
 * @param coordinates - Array of [longitude, latitude] pairs
 * @returns Array of cumulative distances in kilometers
 */
export function calculateCumulativeDistances(coordinates: [number, number][]): number[] {
  const distances: number[] = [0]
  let totalDistance = 0
  
  for (let i = 1; i < coordinates.length; i++) {
    const [lon1, lat1] = coordinates[i - 1]
    const [lon2, lat2] = coordinates[i]
    const segmentDistance = getDistanceBetweenPoints(lat1, lon1, lat2, lon2)
    totalDistance += segmentDistance
    distances.push(totalDistance / 1000) // Convert to km
  }
  
  return distances
}

/**
 * Check if two points are within a specified distance
 * @param lat1 - Latitude of first point
 * @param lon1 - Longitude of first point
 * @param lat2 - Latitude of second point
 * @param lon2 - Longitude of second point
 * @param radiusMeters - Radius to check in meters
 * @returns True if points are within the specified radius
 */
export function arePointsWithinRadius(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
  radiusMeters: number
): boolean {
  const distance = getDistanceBetweenPoints(lat1, lon1, lat2, lon2)
  return distance <= radiusMeters
}

/**
 * Format distance for display
 * @param distanceKm - Distance in kilometers
 * @param includeUnit - Whether to include the unit in the output
 * @returns Formatted distance string
 */
export function formatDistance(distanceKm: number, includeUnit = true): string {
  if (distanceKm < 1) {
    const meters = Math.round(distanceKm * 1000)
    return includeUnit ? `${meters}m` : meters.toString()
  } else if (distanceKm < 10) {
    return includeUnit ? `${distanceKm.toFixed(1)}km` : distanceKm.toFixed(1)
  } else {
    return includeUnit ? `${Math.round(distanceKm)}km` : Math.round(distanceKm).toString()
  }
}